import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Film, Loader2, UploadCloud, X } from 'lucide-react';
import { useI18n } from '../../i18n/I18nContext';
import ProgressBar from '../ProgressBar/ProgressBar';
import {
  MAX_VIDEO_BYTES,
  VIDEO_ACCEPT,
  confirmLessonVideo,
  getLessonVideoStatus,
  putLessonVideoFile,
  requestLessonVideoUpload,
  videoContentType,
} from '../../api/lessonVideo';
import './LessonVideoUpload.css';

/**
 * LessonVideoUpload — put a video on a lesson from the CRM.
 *
 *   <LessonVideoUpload lesson={lesson} onChange={(updated) => replaceLesson(updated)} />
 *
 * Pick or drop a file; it goes straight to the video bucket with real progress (Yuklanmoqda N%),
 * then the backend converts it to HLS (Tayyorlanmoqda, polled), then the lesson plays it (Tayyor).
 * On any failure: Xatolik with Qayta urinish, which re-sends the file, re-runs the conversion on
 * the uploaded file, or asks for another file, whichever can help.
 *
 * Props:
 * - lesson: { id, videoMediaId?, videoDuration? }
 * - onChange(updatedLesson): called once the new video is live on the lesson, with
 *   { ...lesson, videoMediaId, videoDuration }, so the parent's preview plays it.
 *
 * Re-mounting (a page reload) picks up an upload still being prepared, or the last failure.
 * Unmounting during the upload cancels it; the conversion carries on server-side.
 */

const POLL_MS = 4000;

// Server failures another run on the SAME uploaded file can fix. The rest need a different file.
const RERUN_FIXES = new Set(['STALLED', 'LAUNCH_FAILED', 'STORAGE_ERROR', 'INTERNAL']);
// Upload or confirm failures worth repeating as they were (a blip, an expired link, a busy server).
const REPEAT_FIXES = new Set(['uploadFailed', 'linkExpired', 'unavailable', 'generic']);

const SERVER_ERROR_KEYS = {
  NOT_A_VIDEO: 'notVideo',
  SOURCE_MISSING: 'sourceMissing',
  TRANSCODE_FAILED: 'transcodeFailed',
  STALLED: 'stalled',
  LAUNCH_FAILED: 'unavailable',
  STORAGE_ERROR: 'generic',
  INTERNAL: 'generic',
};

const API_ERROR_KEYS = {
  UNSUPPORTED_VIDEO_TYPE: 'unsupportedType',
  VIDEO_TOO_LARGE: 'tooLarge',
  UPLOAD_NOT_FOUND: 'sourceMissing',
  VIDEO_UPLOAD_UNAVAILABLE: 'unavailable',
};

/** An error from apiClient: { code: httpStatus, error: { code, message } }. */
function apiErrorKey(err) {
  const known = API_ERROR_KEYS[err?.error?.code];
  if (known) return known;
  if (err?.code === 403) return 'forbidden';
  if (err?.code === 503) return 'unavailable';
  return 'generic';
}

/** An error from the PUT to the bucket. */
function storageErrorKey(err) {
  const body = String(err?.body || '');
  if (/ExpiredToken|expired/i.test(body)) return 'linkExpired';
  if (/EntityTooLarge|content-length-range/i.test(body)) return 'tooLarge';
  if (err?.status === 403) return 'unavailable';
  return 'uploadFailed';
}

export function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n >= 1e9) return `${(n / 1e9).toFixed(n >= 1e10 ? 0 : 1).replace(/\.0$/, '')} GB`;
  if (n >= 1e6) return `${Math.round(n / 1e6)} MB`;
  return `${Math.max(1, Math.round(n / 1e3))} KB`;
}

export function formatDuration(seconds) {
  const total = Math.round(Number(seconds) || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

function LessonVideoUpload({ lesson, onChange }) {
  const { t } = useI18n();
  const titleId = useId();
  const lessonId = lesson?.id;

  // loading | idle | uploading | processing | ready | error
  const [phase, setPhase] = useState('loading');
  const [percent, setPercent] = useState(0);
  const [bytes, setBytes] = useState({ loaded: 0, total: 0 });
  const [processingPercent, setProcessingPercent] = useState(null);
  const [upload, setUpload] = useState(null);
  const [fileName, setFileName] = useState('');
  // { key, vars?, stage: 'local' | 'upload' | 'confirm' | 'server', code? }
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);

  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const objectKeyRef = useRef(null);
  const abortRef = useRef(null);
  const pollRef = useRef(null);
  const pollFnRef = useRef(null);
  const aliveRef = useRef(true);
  const lessonRef = useRef(lesson);
  const onChangeRef = useRef(onChange);
  lessonRef.current = lesson;
  onChangeRef.current = onChange;

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const applyStatus = useCallback((status) => {
    if (!aliveRef.current || !status) return;
    const current = status.upload;
    setUpload(current);
    stopPolling();

    if (!current) {
      setPhase('idle');
      return;
    }
    if (current.objectKey) objectKeyRef.current = current.objectKey;
    setFileName(current.fileName || '');

    if (current.state === 'PROCESSING') {
      setPhase('processing');
      setProcessingPercent(typeof current.progress === 'number' ? current.progress : null);
      pollRef.current = setTimeout(() => pollFnRef.current?.(), POLL_MS);
      return;
    }
    if (current.state === 'FAILED') {
      setError({
        key: SERVER_ERROR_KEYS[current.errorCode] || 'generic',
        stage: 'server',
        code: current.errorCode,
      });
      setPhase('error');
      return;
    }
    // READY. An upload the lesson no longer plays (replaced by an import since) is history.
    if (!current.live) {
      setPhase('idle');
      return;
    }
    setPhase('ready');
    const before = lessonRef.current;
    if (before && before.videoMediaId !== status.videoMediaId) {
      onChangeRef.current?.({
        ...before,
        videoMediaId: status.videoMediaId,
        videoDuration: status.videoDuration ?? before.videoDuration,
      });
    }
  }, [stopPolling]);

  const poll = useCallback(async () => {
    pollRef.current = null;
    try {
      applyStatus(await getLessonVideoStatus(lessonRef.current.id));
    } catch {
      // A failed poll is usually a blip: keep watching rather than showing an error.
      if (aliveRef.current) pollRef.current = setTimeout(() => pollFnRef.current?.(), POLL_MS * 2);
    }
  }, [applyStatus]);
  pollFnRef.current = poll;

  // Load the lesson's upload state; a reload resumes an upload that is still being prepared.
  useEffect(() => {
    aliveRef.current = true;
    if (!lessonId) return undefined;
    setPhase('loading');
    getLessonVideoStatus(lessonId)
      .then(applyStatus)
      .catch(() => aliveRef.current && setPhase('idle'));
    return () => {
      aliveRef.current = false;
      stopPolling();
      if (abortRef.current) abortRef.current.abort();
    };
  }, [lessonId, applyStatus, stopPolling]);

  // Closing the tab mid-upload loses the file: ask first.
  useEffect(() => {
    if (phase !== 'uploading') return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = t('lessonVideo.leaveWarning');
      return event.returnValue;
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [phase, t]);

  const fail = (next) => {
    if (!aliveRef.current) return;
    setError(next);
    setPhase('error');
  };

  const confirm = async (objectKey, name) => {
    setError(null);
    setPhase('processing');
    setProcessingPercent(null);
    try {
      applyStatus(await confirmLessonVideo(lessonRef.current.id, objectKey, name));
    } catch (err) {
      fail({ key: apiErrorKey(err), stage: 'confirm', code: err?.error?.code });
    }
  };

  const start = async (file) => {
    setError(null);
    if (!videoContentType(file)) {
      fail({ key: 'unsupportedType', stage: 'local' });
      return;
    }
    if (file.size > MAX_VIDEO_BYTES) {
      fail({ key: 'tooLarge', stage: 'local' });
      return;
    }

    fileRef.current = file;
    objectKeyRef.current = null;
    setFileName(file.name);
    setPercent(0);
    setBytes({ loaded: 0, total: file.size });
    setPhase('uploading');

    const controller = new AbortController();
    abortRef.current = controller;
    let ticket;
    try {
      ticket = await requestLessonVideoUpload(lessonRef.current.id, file);
    } catch (err) {
      abortRef.current = null;
      if (!controller.signal.aborted) fail({ key: apiErrorKey(err), stage: 'upload', code: err?.error?.code });
      return;
    }
    try {
      await putLessonVideoFile(ticket, file, {
        signal: controller.signal,
        onProgress: (loaded, total) => {
          if (!aliveRef.current) return;
          setBytes({ loaded, total });
          setPercent(Math.min(99, Math.floor((loaded / (total || 1)) * 100)));
        },
      });
    } catch (err) {
      abortRef.current = null;
      if (err?.cancelled) {
        if (aliveRef.current) setPhase('idle');
        return;
      }
      fail({ key: storageErrorKey(err), stage: 'upload' });
      return;
    }
    abortRef.current = null;
    if (!aliveRef.current) return;
    setPercent(100);
    objectKeyRef.current = ticket.objectKey;
    await confirm(ticket.objectKey, file.name);
  };

  const openPicker = () => inputRef.current?.click();

  const takeFiles = (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    if (list.length > 1) {
      fail({ key: 'oneFile', stage: 'local' });
      return;
    }
    start(list[0]);
  };

  const onInputChange = (event) => {
    takeFiles(event.target.files);
    event.target.value = '';
  };

  const busy = phase === 'uploading' || phase === 'processing' || phase === 'loading';
  const onDragOver = (event) => {
    if (busy) return;
    event.preventDefault();
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDragging(false);
  };
  const onDrop = (event) => {
    if (busy) return;
    event.preventDefault();
    setDragging(false);
    takeFiles(event.dataTransfer?.files);
  };

  // What "Qayta urinish" does for this failure, or null when only another file can help.
  const retryAction = (() => {
    if (!error) return null;
    const file = fileRef.current;
    const objectKey = objectKeyRef.current;
    if (error.stage === 'upload' && file && REPEAT_FIXES.has(error.key)) {
      return () => start(file);
    }
    if (error.stage === 'confirm' && objectKey && REPEAT_FIXES.has(error.key)) {
      return () => confirm(objectKey, fileName);
    }
    if (error.stage === 'confirm' && file && error.key === 'sourceMissing') {
      return () => start(file);
    }
    if (error.stage === 'server' && objectKey && RERUN_FIXES.has(error.code)) {
      return () => confirm(objectKey);
    }
    return null;
  })();

  const cancel = () => abortRef.current?.abort();

  const maxLabel = formatBytes(MAX_VIDEO_BYTES);
  const errorText = error ? t(`lessonVideo.errors.${error.key}`, { max: maxLabel }) : '';
  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={VIDEO_ACCEPT}
      className="lvu-file-input"
      aria-label={t('lessonVideo.choose')}
      tabIndex={-1}
      onChange={onInputChange}
    />
  );

  return (
    <section
      className={`lvu${dragging ? ' is-dragging' : ''}`}
      aria-labelledby={titleId}
      onDragOver={onDragOver}
      onDragEnter={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-phase={phase}
    >
      <div className="lvu-head">
        <Film size={16} aria-hidden="true" />
        <h3 id={titleId} className="lvu-title">{t('lessonVideo.title')}</h3>
      </div>

      {phase === 'loading' && (
        <div className="lvu-panel lvu-panel--quiet">
          <Loader2 size={18} className="lvu-spin" aria-hidden="true" />
        </div>
      )}

      {phase === 'idle' && (
        <div className="lvu-drop">
          <UploadCloud size={28} className="lvu-drop-icon" aria-hidden="true" />
          <p className="lvu-drop-title">
            {dragging ? t('lessonVideo.dropActive') : t('lessonVideo.dropTitle')}
          </p>
          <p className="lvu-drop-or">{t('lessonVideo.or')}</p>
          <button type="button" className="lvu-btn lvu-btn--primary" onClick={openPicker}>
            {t('lessonVideo.choose')}
          </button>
          <p className="lvu-hint">{t('lessonVideo.hint', { max: maxLabel })}</p>
          {lesson?.videoMediaId && <p className="lvu-note">{t('lessonVideo.hasVideo')}</p>}
        </div>
      )}

      {phase === 'uploading' && (
        <div className="lvu-panel">
          <div className="lvu-row">
            <span className="lvu-state">{t('lessonVideo.uploading', { percent })}</span>
            <span className="lvu-meta">
              {t('lessonVideo.uploadedOf', {
                done: formatBytes(bytes.loaded),
                total: formatBytes(bytes.total),
              })}
            </span>
          </div>
          <ProgressBar value={percent} label={t('lessonVideo.progressLabel')} />
          <div className="lvu-row">
            <span className="lvu-file" title={fileName}>{fileName}</span>
            <button type="button" className="lvu-btn lvu-btn--ghost" onClick={cancel}>
              <X size={14} aria-hidden="true" />
              {t('lessonVideo.cancel')}
            </button>
          </div>
        </div>
      )}

      {phase === 'processing' && (
        <div className="lvu-panel">
          <div className="lvu-row">
            <span className="lvu-state" role="status">
              <Loader2 size={16} className="lvu-spin" aria-hidden="true" />
              {processingPercent == null
                ? t('lessonVideo.processing')
                : t('lessonVideo.processingPercent', { percent: processingPercent })}
            </span>
            <span className="lvu-file" title={fileName}>{fileName}</span>
          </div>
          {processingPercent == null ? (
            <div className="lvu-indeterminate" aria-hidden="true"><span /></div>
          ) : (
            <ProgressBar value={processingPercent} tone="info" label={t('lessonVideo.processing')} />
          )}
          <p className="lvu-hint">{t('lessonVideo.processingHint')}</p>
        </div>
      )}

      {phase === 'ready' && (
        <div className="lvu-panel lvu-panel--ok">
          <div className="lvu-row">
            <span className="lvu-state lvu-state--ok" role="status">
              <CheckCircle2 size={16} aria-hidden="true" />
              {t('lessonVideo.ready')}
            </span>
            <span className="lvu-meta">
              {[fileName, upload?.duration ? formatDuration(upload.duration) : null]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
          <p className="lvu-hint">{t('lessonVideo.readyHint')}</p>
          <div className="lvu-actions">
            <button type="button" className="lvu-btn" onClick={openPicker}>
              {t('lessonVideo.replace')}
            </button>
          </div>
        </div>
      )}

      {phase === 'error' && error && (
        <div className="lvu-panel lvu-panel--error" role="alert">
          <div className="lvu-row">
            <span className="lvu-state lvu-state--error">
              <AlertCircle size={16} aria-hidden="true" />
              {t('lessonVideo.error')}
            </span>
            {fileName && <span className="lvu-file" title={fileName}>{fileName}</span>}
          </div>
          <p className="lvu-message">{errorText}</p>
          {lesson?.videoMediaId && <p className="lvu-hint">{t('lessonVideo.previousKept')}</p>}
          <div className="lvu-actions">
            {retryAction ? (
              <>
                <button type="button" className="lvu-btn lvu-btn--primary" onClick={retryAction}>
                  {t('lessonVideo.retry')}
                </button>
                <button type="button" className="lvu-btn" onClick={openPicker}>
                  {t('lessonVideo.chooseAnother')}
                </button>
              </>
            ) : (
              <button type="button" className="lvu-btn lvu-btn--primary" onClick={openPicker}>
                {t('lessonVideo.chooseAnother')}
              </button>
            )}
          </div>
        </div>
      )}

      {fileInput}
    </section>
  );
}

export default LessonVideoUpload;
